'use client'

import { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, ShieldCheck, Loader2, Package, ArrowRight } from 'lucide-react'
import VehicleBrandLogo from '@/components/VehicleBrandLogo'
import CategoryIcon from '@/components/CategoryIcon'

interface FitmentProduct {
  id: string
  name: string
  price: number
  currency?: string
  images?: string[]
  image?: string
  make?: string
  model?: string
  category?: string
  seller?: { name?: string; phone?: string }
  sellerId?: string
  sellerName?: string
}

interface VinFitmentCarouselProps {
  make: string
  model: string
  year: number
  onProductClick?: (product: FitmentProduct) => void
  onShopAll?: () => void
}

function formatPrice(amount: number, currency = 'NGN'): string {
  const symbols: Record<string, string> = {
    NGN: '₦', GHS: 'GH₵', KES: 'KSh', ZAR: 'R', USD: '$', GBP: '£', EUR: '€',
  }
  const symbol = symbols[currency] || currency
  return `${symbol}${amount.toLocaleString()}`
}

export function VinFitmentCarousel({ make, model, year, onProductClick, onShopAll }: VinFitmentCarouselProps) {
  const [products, setProducts] = useState<FitmentProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    if (!make) return
    setLoading(true)
    setError(false)

    const apiBase =
      process.env.NEXT_PUBLIC_P4D_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      'https://parts4drive.com'

    const params = new URLSearchParams()
    if (make) params.set('make', make)
    if (model) params.set('model', model)
    params.set('limit', '12')

    fetch(`${apiBase}/api/v1/marketplace/products?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        const list: FitmentProduct[] = Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data?.data)
          ? data.data
          : []
        setProducts(list)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [make, model, year])

  const updateScrollState = () => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 8)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8)
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    window.addEventListener('resize', updateScrollState)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [products])

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir === 'left' ? -260 : 260, behavior: 'smooth' })
  }

  if (loading) {
    return (
      <div className="relative z-10 pt-4 border-t border-white/10 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="size-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-white">Compatible Parts for your Vehicle</span>
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="w-40 shrink-0 rounded-2xl bg-white/5 border border-white/10 h-48 animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  if (error || products.length === 0) {
    return (
      <div className="relative z-10 pt-4 border-t border-white/10 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="size-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-white">Compatible Parts</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400 py-3">
          <Package className="size-4" />
          <span>
            No parts listed yet for {year} {make} {model} — check the full catalog.
          </span>
          {onShopAll && (
            <button
              type="button"
              onClick={onShopAll}
              className="ml-auto text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-1 shrink-0"
            >
              Browse all <ArrowRight className="size-3" />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="relative z-10 pt-4 border-t border-white/10 mt-4">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <VehicleBrandLogo brand={make} size="xs" variant="badge" />
          <ShieldCheck className="size-3.5 text-emerald-400 shrink-0" />
          <span className="text-xs font-semibold text-white truncate">
            {products.length} Compatible Parts — {year} {make} {model}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onShopAll && (
            <button
              type="button"
              onClick={onShopAll}
              className="text-xs text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-1 mr-2"
            >
              <span>Shop All</span>
              <ArrowRight className="size-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className="size-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30 transition-all ml-1"
          >
            <ChevronLeft className="size-3.5 text-white" />
          </button>
          <button
            type="button"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className="size-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30 transition-all"
          >
            <ChevronRight className="size-3.5 text-white" />
          </button>
        </div>
      </div>

      {/* Carousel track */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-1 scrollbar-none snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none' }}
      >
        {products.map(product => {
          const imgSrc = product.images?.[0] || product.image
          const price = typeof product.price === 'number' ? product.price : 0

          return (
            <button
              key={product.id}
              type="button"
              onClick={() => onProductClick?.(product as any)}
              className="group snap-start shrink-0 w-40 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-400/50 hover:bg-white/10 transition-all overflow-hidden flex flex-col text-left active:scale-95"
            >
              {/* Product image (1:1 Square) */}
              <div className="relative w-full aspect-square bg-white/5 overflow-hidden">
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="size-8 text-zinc-600" />
                  </div>
                )}
                {/* Fitment badge with Brand Logo */}
                <span className="absolute top-1.5 left-1.5 bg-emerald-500/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-xs">
                  <VehicleBrandLogo brand={product.make || make} size="xs" variant="badge" className="size-4 p-0" />
                  <span>Fits {make}</span>
                </span>
              </div>

              {/* Info */}
              <div className="flex flex-col gap-1 p-2.5 flex-1 justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-white leading-tight line-clamp-2 h-7">
                    {product.name}
                  </p>
                  {product.category && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <CategoryIcon category={product.category} size="xs" variant="raw" className="size-3 shrink-0" />
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wide truncate">{product.category}</p>
                    </div>
                  )}
                </div>
                <p className="text-xs font-bold text-blue-300 mt-auto pt-1">
                  {formatPrice(price, product.currency)}
                </p>
              </div>
            </button>
          )
        })}

        {/* "View all" end card */}
        {onShopAll && (
          <button
            type="button"
            onClick={onShopAll}
            className="snap-start shrink-0 w-32 rounded-2xl bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 transition-all flex flex-col items-center justify-center gap-2 text-blue-300 active:scale-95"
          >
            <ArrowRight className="size-5" />
            <span className="text-[11px] font-semibold text-center px-2">See All Compatible</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default VinFitmentCarousel
